import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Send, CheckCircle2 } from "lucide-react";

const METHODS = ["stripe", "zelle", "venmo", "cash", "check", "other"];

const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x.toISOString().slice(0, 10);
};
const addDays = (iso, n) => {
  const x = new Date(iso + "T00:00:00");
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};
const fmtDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString([], { month: "short", day: "numeric" });

/** Weekly Payout Run — one-click settle of a driver's whole week (Zelle/Venmo/cash/check). */
export default function WeeklyPayoutRun({ onPaid }) {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [paying, setPaying] = useState(null); // driver row
  const [payAllStep, setPayAllStep] = useState(0); // 0 idle, 1 confirm
  const [payAllBusy, setPayAllBusy] = useState(false);

  const payAll = async () => {
    if (payAllStep === 0) { setPayAllStep(1); setTimeout(() => setPayAllStep(0), 5000); return; }
    setPayAllStep(0); setPayAllBusy(true);
    try {
      const r = await api.post("/payouts/weekly/pay-all", { week_start: weekStart, method: "stripe", send_stub: true });
      const d = r.data;
      const fails = d.results.filter(x => !x.ok);
      if (d.paid_drivers > 0) toast.success(`Deposited $${d.paid_total.toFixed(2)} to ${d.paid_drivers} driver bank account(s)`);
      fails.forEach(f => toast.warning(`${f.driver_name}: ${f.error}`));
      if (!d.paid_drivers && !fails.length) toast.info("Nothing to pay this week");
      reload(weekStart); onPaid?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk payout failed"); }
    finally { setPayAllBusy(false); }
  };

  const reload = useCallback(async (ws) => {
    setBusy(true);
    try {
      const r = await api.get("/payouts/weekly", { params: { week_start: ws } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load weekly payouts");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { reload(weekStart); }, [weekStart, reload]);

  const isCurrentWeek = weekStart === mondayOf(new Date());

  return (
    <div data-testid="weekly-payout-run">
      {/* Week navigation */}
      <div className="flex items-center gap-3 mb-4">
        <button data-testid="week-prev" onClick={() => setWeekStart(addDays(weekStart, -7))}
          className="btn-ghost !py-1.5 !px-3"><ChevronLeft size={14}/></button>
        <div className="text-sm text-[#F5F2EB]" data-testid="week-label">
          Week of <span className="text-[#D4AF37] font-serif">{fmtDay(weekStart)} – {fmtDay(addDays(weekStart, 6))}</span>
          {isCurrentWeek && <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-emerald-300">current</span>}
        </div>
        <button data-testid="week-next" onClick={() => setWeekStart(addDays(weekStart, 7))}
          disabled={isCurrentWeek} className="btn-ghost !py-1.5 !px-3 disabled:opacity-30"><ChevronRight size={14}/></button>
        {data && (
          <div className="ml-auto flex items-center gap-3">
            <div className="text-[11px] text-[#A1A1AA]">
              <span className="text-[#D4AF37] font-serif text-base" data-testid="week-total-owed">${(data.total_owed || 0).toFixed(2)}</span> still owed
              <span className="mx-2 text-[#71717A]">·</span>
              <span className="text-emerald-300">${(data.total_paid || 0).toFixed(2)}</span> paid
            </div>
            {data.total_owed > 0 && (
              <button data-testid="pay-all-stripe" onClick={payAll} disabled={payAllBusy}
                className={`!py-1.5 !px-4 text-[10px] ${payAllStep === 1 ? "btn-gold" : "btn-ghost"}`}>
                {payAllBusy ? "Depositing…" : payAllStep === 1 ? `Confirm · deposit $${data.total_owed.toFixed(2)}` : "Deposit all to banks"}
              </button>
            )}
          </div>
        )}
      </div>

      {busy && !data ? <div className="text-[#A1A1AA] text-sm">Loading…</div> : null}
      {data && data.drivers.length === 0 && (
        <div className="text-[#A1A1AA] text-sm border border-white/10 bg-[#0A0A0A] p-6 text-center" data-testid="week-empty">
          No completed driver trips this week.
        </div>
      )}

      <div className="space-y-3">
        {data && data.drivers.map(d => (
          <div key={d.driver_id} data-testid={`payout-driver-${d.driver_id}`} className="border border-white/10 bg-[#0A0A0A]">
            <div className="px-4 py-3 flex items-center gap-4 flex-wrap">
              <div>
                <div className="text-[#F5F2EB] text-sm">{d.driver_name}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#71717A] mt-0.5">
                  {d.trips.length} trip{d.trips.length === 1 ? "" : "s"} · {d.paid_count} paid · {d.unpaid_count} unpaid
                </div>
              </div>
              <div className="ml-auto flex items-center gap-4">
                {d.total_owed > 0 ? (
                  <span className="text-[#D4AF37] font-serif text-xl" data-testid={`owed-${d.driver_id}`}>${d.total_owed.toFixed(2)}</span>
                ) : (
                  <span className="text-emerald-300 text-[11px] uppercase tracking-[0.18em] flex items-center gap-1" data-testid={`settled-${d.driver_id}`}>
                    <CheckCircle2 size={13}/>Week settled
                  </span>
                )}
                <button data-testid={`toggle-trips-${d.driver_id}`}
                  onClick={() => setExpanded(x => ({ ...x, [d.driver_id]: !x[d.driver_id] }))}
                  className="btn-ghost !py-1.5 !px-3 text-[10px]">
                  {expanded[d.driver_id] ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}Trips
                </button>
                {d.total_owed > 0 && (
                  <button data-testid={`pay-week-${d.driver_id}`} onClick={() => setPaying(d)}
                    className="btn-gold !py-1.5 !px-4 text-[10px]"><Send size={12}/>Pay week</button>
                )}
              </div>
            </div>

            {expanded[d.driver_id] && (
              <div className="border-t border-white/10">
                <table className="w-full text-[11px]">
                  <thead className="bg-black/40 text-[9px] uppercase tracking-[0.18em] text-[#71717A]">
                    <tr>
                      <th className="text-left px-4 py-2">Trip</th>
                      <th className="text-left px-2">Date</th>
                      <th className="text-left px-2">Route</th>
                      <th className="text-right px-2">Driver pay</th>
                      <th className="text-right px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.trips.map(t => (
                      <tr key={t.booking_id} className="border-t border-white/5" data-testid={`payout-trip-${t.booking_id}`}>
                        <td className="px-4 py-2 font-mono text-[#D4AF37]">{t.confirmation_code}</td>
                        <td className="px-2 text-[#A1A1AA]">{(t.pickup_datetime || "").slice(0, 10)}</td>
                        <td className="px-2 text-[#A1A1AA] truncate max-w-[280px]">{t.pickup_address} → {t.dropoff_address || "—"}</td>
                        <td className="px-2 text-right font-mono text-[#F5F2EB]">
                          ${t.amount.toFixed(2)}
                          {t.suggested && <span className="ml-1 text-[9px] text-[#71717A]" title="No settlement yet — default 77% of trip fare">auto</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className={`text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 border ${
                            t.settlement_status === "paid" ? "text-emerald-300 border-emerald-400/40"
                            : t.settlement_status === "approved" ? "text-[#D4AF37] border-[#D4AF37]/40"
                            : "text-[#A1A1AA] border-white/10"
                          }`}>{t.settlement_status === "paid" ? `paid · ${t.method || ""}` : t.settlement_status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {paying && (
        <PayWeekModal driver={paying} weekStart={weekStart}
          onClose={() => setPaying(null)}
          onDone={() => { setPaying(null); reload(weekStart); onPaid?.(); }}/>
      )}
    </div>
  );
}

function PayWeekModal({ driver, weekStart, onClose, onDone }) {
  const [method, setMethod] = useState("zelle");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [sendStub, setSendStub] = useState(Boolean(driver.driver_email));
  const [busy, setBusy] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [stripeReady, setStripeReady] = useState(Boolean(driver.stripe_payouts_enabled));

  const stripeSelected = method === "stripe";

  const checkStripe = async () => {
    try {
      const r = await api.get(`/stripe/drivers/${driver.driver_id}/status`);
      setStripeReady(Boolean(r.data.payouts_enabled));
      return r.data;
    } catch (_e) { return null; }
  };

  useEffect(() => { if (stripeSelected && !stripeReady) checkStripe(); }, [stripeSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendSetupLink = async () => {
    setLinkBusy(true);
    try {
      const r = await api.post(`/stripe/drivers/${driver.driver_id}/onboarding-link`, {
        send_sms: Boolean(driver.driver_phone), send_email: Boolean(driver.driver_email),
      });
      const via = r.data.sent.length ? ` via ${r.data.sent.join(" + ")}` : "";
      toast.success(`Direct-deposit setup link sent to ${driver.driver_name}${via}`);
      if (!r.data.sent.length) {
        await navigator.clipboard?.writeText(r.data.url).catch(() => {});
        toast.info("No phone/email on file — link copied to clipboard");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create setup link"); }
    finally { setLinkBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payouts/weekly/pay", {
        driver_id: driver.driver_id, week_start: weekStart,
        method, paid_date: paidDate, notes: notes || null, send_stub: sendStub,
      });
      toast.success(method === "stripe"
        ? `$${r.data.total.toFixed(2)} deposit initiated to ${driver.driver_name}'s bank`
        : `Paid ${r.data.paid_count} trip(s) · $${r.data.total.toFixed(2)} to ${driver.driver_name}${r.data.stub_sent ? " · pay stub emailed" : ""}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed"); }
    finally { setBusy(false); }
  };

  const blocked = stripeSelected && !stripeReady;

  return (
    <div data-testid="pay-week-modal" className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#050505] border border-white/10 max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-serif text-xl">Pay the week</h3>
          <button data-testid="pay-week-close" onClick={onClose} className="text-[#71717A] hover:text-[#F5F2EB] text-xl leading-none">×</button>
        </div>
        <div className="text-sm text-[#A1A1AA] mb-4">
          <span className="text-[#D4AF37] font-serif text-lg">${driver.total_owed.toFixed(2)}</span> to {driver.driver_name}
          <span className="mx-1 text-[#71717A]">·</span>{driver.unpaid_count} unpaid trip{driver.unpaid_count === 1 ? "" : "s"}
        </div>

        {(driver.payout_zelle || driver.payout_venmo || driver.payout_preferred) && (
          <div className="text-[11px] text-[#A1A1AA] border border-white/10 bg-white/[0.03] px-3 py-2 mb-4" data-testid="driver-payout-prefs-hint">
            <span className="text-[#D4AF37] uppercase tracking-[0.14em] text-[9px] mr-2">Driver prefers</span>
            {driver.payout_preferred && <span className="capitalize">{driver.payout_preferred.replace("_", " ")}</span>}
            {driver.payout_zelle && <> · Zelle: <span className="text-[#F5F2EB]">{driver.payout_zelle}</span></>}
            {driver.payout_venmo && <> · Venmo: <span className="text-[#F5F2EB]">@{driver.payout_venmo}</span></>}
          </div>
        )}

        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Paid via</label>
        <div className="flex gap-2 mb-3 flex-wrap">
          {METHODS.map(m => (
            <button key={m} data-testid={`pay-method-${m}`} onClick={() => setMethod(m)}
              className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border ${
                method === m ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-[#A1A1AA]"
              }`}>{m === "stripe" ? "Bank deposit" : m}</button>
          ))}
        </div>

        {stripeSelected && (
          <div data-testid="stripe-deposit-status" className={`mb-4 px-3 py-2.5 border text-[11px] ${
            stripeReady ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
            {stripeReady ? (
              <>Direct deposit ready — ${driver.total_owed.toFixed(2)} will be sent to {driver.driver_name}'s bank (arrives in ~2 business days).</>
            ) : (
              <div className="flex items-center gap-3 flex-wrap">
                <span>{driver.driver_name} hasn't set up direct deposit yet.</span>
                <button data-testid="send-stripe-setup-link" onClick={sendSetupLink} disabled={linkBusy}
                  className="border border-amber-400/50 px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] hover:bg-amber-500/20">
                  {linkBusy ? "Sending…" : "Send setup link"}
                </button>
                <button data-testid="recheck-stripe-status" onClick={checkStripe}
                  className="text-[10px] underline opacity-70 hover:opacity-100">Re-check</button>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Payment date</label>
            <input type="date" data-testid="pay-week-date" value={paidDate} onChange={e => setPaidDate(e.target.value)}
              className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]"/>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Notes (optional)</label>
            <input data-testid="pay-week-notes" value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full bg-transparent border border-white/10 px-2 py-1.5 text-sm text-[#F5F2EB]" placeholder="Zelle ref #…"/>
          </div>
        </div>

        <label className={`flex items-center gap-2 text-xs mb-5 ${driver.driver_email ? "text-[#A1A1AA]" : "text-[#52525B]"}`}>
          <input type="checkbox" data-testid="pay-week-stub" checked={sendStub} disabled={!driver.driver_email}
            onChange={e => setSendStub(e.target.checked)}/>
          Email pay stub to driver {driver.driver_email ? `(${driver.driver_email})` : "(no email on file)"}
        </label>

        <div className="flex gap-2">
          <button data-testid="pay-week-confirm" onClick={submit} disabled={busy || blocked}
            className="btn-gold flex-1 justify-center disabled:opacity-40">
            {busy ? "…" : stripeSelected ? `Deposit $${driver.total_owed.toFixed(2)} to bank` : `Confirm · $${driver.total_owed.toFixed(2)}`}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </div>
  );
}
